// Pure helpers for the missing-effective-date issue's decide flow
// (Grant 2026-07-10 QA, Brandon Ream impersonation session). The
// sr-missing-effective-date recommendation used to be verb "review"
// ("Open the record to set the start date") with nowhere on the
// record page to actually type it — a dead end, same problem
// client-since-issue.ts and expiration-issue.ts already fixed for
// their detectors. The rail now renders a date input and sends
// params.effective_date; the backend writes it onto the record
// (audited, 60s-undoable) and resolves the issue.
//
// Same pattern + conventions as client-since-issue.ts / expiration-
// issue.ts. Root-safe (imports nothing) so it's unit-tested from
// tests/web/.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface EffectiveDateIssueLike {
  detectorSlug: string;
}

// Issues whose "verify" carries an effective (start) date to the backend.
export function isEffectiveDateIssue(issue: EffectiveDateIssueLike): boolean {
  return issue.detectorSlug === "sr-missing-effective-date";
}

// Mirror of shared/lib/iso-date.ts (kept dependency-free so this module
// stays root-safe): strict YYYY-MM-DD that round-trips through Date.
export function isValidEffectiveDateInput(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

// Plain-language before→after for the date-carrying verify (UX rule
// #3). Entering the date IS the action — the backend 400s without it.
export function effectiveDateConsequence(
  issue: EffectiveDateIssueLike,
  date: string,
): string | null {
  if (!isEffectiveDateIssue(issue)) return null;
  if (date && isValidEffectiveDateInput(date)) {
    return `Sets the record's start (effective) date to ${date} and resolves this issue. Renewal/rate-watch scheduling and reporting anchor on it. Audited; reversible for 60 seconds.`;
  }
  return "Enter the start date from the policy, loan document, or signed agreement first — setting it is the whole action here.";
}
